import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBrandManager } from "@/hooks/useBrandManager";
import { useEventLeadDialog } from "@/contexts/EventLeadDialogContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { EVENT_LEAD_TYPE_OPTIONS } from "@/types/events";
import { triggerImmediateEmailProcessing } from "@/utils/triggerImmediateEmailProcessing";

// Status options with colors - 'scanned' is disabled (reserved for public path)
const UI_LEAD_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-gray-500', disabled: false },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-500', disabled: false },
  { value: 'attended', label: 'Attended', color: 'bg-purple-500', disabled: false },
  { value: 'scanned', label: 'Scanned', color: 'bg-amber-500', disabled: true },
  { value: 'converted', label: 'Converted', color: 'bg-green-500', disabled: false },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500', disabled: false },
];

const syncLeadToContacts = async (
  brandId: string,
  lead: { name?: string | null; email: string; phone?: string | null }
): Promise<string | null> => {
  if (!lead.email) return null;
  
  const nameParts = (lead.name || '').trim().split(/\s+/);
  const firstName = nameParts[0] || lead.email.split('@')[0];
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
  
  try {
    const { data: contactId, error } = await supabase.rpc('upsert_os_contact', {
      p_brand_id: brandId,
      p_email: lead.email,
      p_first_name: firstName,
      p_last_name: lastName,
      p_phone: lead.phone || null,
      p_module: ['Events'],
      p_activity: ['lead'],
      p_organization: null,
      p_title: null,
      p_user_id: null,
    });
    
    if (error) {
      console.error('Error syncing lead to contacts:', error);
      return null;
    }
    
    return contactId;
  } catch (error) {
    console.error('Error syncing lead to contacts:', error);
    return null;
  }
};

const enrollLeadInEventCampaigns = async (
  brandId: string,
  eventId: string,
  leadId: string,
  recipientEmail: string,
  recipientName: string | null,
  recipientData: Record<string, unknown>
): Promise<number> => {
  let enrolledCount = 0;
  
  try {
    // Find active campaigns linked to this event
    const { data: campaigns, error: campaignsError } = await supabase
      .from('os_event_campaigns')
      .select('id, name')
      .eq('brand_id', brandId)
      .eq('event_id', eventId)
      .eq('status', 'active');

    if (campaignsError || !campaigns?.length) {
      console.log('No active campaigns found for event:', eventId);
      return 0;
    }

    for (const campaign of campaigns) {
      // Get campaign emails sorted by sequence
      const { data: campaignEmails, error: emailsError } = await supabase
        .from('os_event_campaign_emails')
        .select('id, sequence_order, delay_days, delay_hours, send_conditions')
        .eq('campaign_id', campaign.id)
        .order('sequence_order', { ascending: true });

      if (emailsError || !campaignEmails?.length) {
        console.log('No emails found for campaign:', campaign.id);
        continue;
      }

      // Only insert the first email step
      const firstEmail = campaignEmails[0];
      const enrolledAt = new Date();
      const scheduledAt = new Date(enrolledAt);
      scheduledAt.setDate(scheduledAt.getDate() + (firstEmail.delay_days || 0));
      scheduledAt.setHours(scheduledAt.getHours() + (firstEmail.delay_hours || 0));

      const queueItem = {
        brand_id: brandId,
        source_module: 'events' as const,
        campaign_id: campaign.id,
        campaign_email_id: firstEmail.id,
        recipient_id: leadId,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        recipient_data: recipientData as Record<string, string | number | boolean | null>,
        step_order: firstEmail.sequence_order,
        delay_days: firstEmail.delay_days || 0,
        delay_hours: firstEmail.delay_hours || 0,
        status: 'pending' as const,
        enrolled_at: enrolledAt.toISOString(),
        scheduled_at: scheduledAt.toISOString(),
        metadata: firstEmail.send_conditions ? { stop_conditions: firstEmail.send_conditions } : {},
      };

      const { error: queueError } = await supabase
        .from('os_campaign_queue')
        .upsert([queueItem], { onConflict: 'campaign_email_id,recipient_email' });

      if (queueError) {
        console.error('Error enrolling lead in campaign queue:', queueError);
      } else {
        enrolledCount++;
        console.log(`Lead enrolled in campaign: ${campaign.name}`);
      }
    }
  } catch (error) {
    console.error('Error enrolling lead in campaigns:', error);
  }
  
  return enrolledCount;
};

export const GlobalEventLeadDialog: React.FC = () => {
  const { currentBrandId } = useBrandManager();
  const queryClient = useQueryClient();
  const {
    isOpen,
    mode,
    lead,
    formData,
    closeDialog,
    updateFormData,
    onSuccess,
  } = useEventLeadDialog();

  const { data: events = [] } = useQuery({
    queryKey: ["events-for-leads-global", currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return [];
      const { data, error } = await supabase
        .from("os_events")
        .select("id, title")
        .eq("brand_id", currentBrandId)
        .order("title");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!currentBrandId && isOpen,
  });

  const addLeadMutation = useMutation({
    mutationFn: async () => {
      if (!currentBrandId) throw new Error("Brand required");
      
      // Sync to contacts first
      const contactId = await syncLeadToContacts(currentBrandId, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      });
      
      const { data: insertedLead, error } = await supabase.from("os_events_leads").insert({
        brand_id: currentBrandId,
        event_id: formData.event_id,
        email: formData.email,
        name: formData.name || null,
        phone: formData.phone || null,
        notes: formData.notes || null,
        activity: formData.activity || null,
        type: formData.type,
        status: formData.status,
        contact_id: contactId,
      }).select('id').single();
      
      if (error) throw error;

      // Fetch event details for variable substitution
      const { data: eventData } = await supabase
        .from('os_events')
        .select('title, time, address')
        .eq('id', formData.event_id)
        .single();

      // Extract first name from full name
      const nameParts = (formData.name || '').trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Build complete recipient_data with all substitution variables
      const recipientData = {
        email: formData.email,
        name: formData.name || '',
        first_name: firstName,
        last_name: lastName,
        phone: formData.phone || '',
        event_id: formData.event_id,
        event_title: eventData?.title || '',
        event_date: eventData?.time ? new Date(eventData.time).toLocaleDateString() : '',
        event_time: eventData?.time ? new Date(eventData.time).toLocaleTimeString() : '',
        event_location: eventData?.address || '',
        event_address: eventData?.address || '',
      };

      // Enroll lead in active event campaigns
      const enrolledCount = await enrollLeadInEventCampaigns(
        currentBrandId,
        formData.event_id,
        insertedLead.id,
        formData.email,
        formData.name || null,
        recipientData
      );

      // Trigger immediate processing if enrolled in campaigns (no 5-min wait)
      if (enrolledCount > 0) {
        await triggerImmediateEmailProcessing(currentBrandId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-leads"] });
      queryClient.invalidateQueries({ queryKey: ["campaign-queue"] });
      queryClient.invalidateQueries({ queryKey: ["campaign-queue-summary"] });
      toast.success("Lead added and enrolled in campaigns");
      onSuccess?.();
      closeDialog();
    },
    onError: (e: unknown) => {
      toast.error(`Failed to add lead: ${e instanceof Error ? e.message : "Unknown error"}`);
    },
  });

  const updateLeadMutation = useMutation({
    mutationFn: async () => {
      if (!lead) throw new Error("No lead selected");
      
      // Sync to contacts
      const contactId = await syncLeadToContacts(lead.brand_id, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      });
      
      const { error } = await supabase
        .from("os_events_leads")
        .update({
          event_id: formData.event_id,
          email: formData.email,
          name: formData.name || null,
          phone: formData.phone || null,
          notes: formData.notes || null,
          activity: formData.activity || null,
          type: formData.type,
          status: formData.status,
          ...(contactId ? { contact_id: contactId } : {}),
        })
        .eq("id", lead.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-leads"] });
      toast.success("Lead updated successfully");
      onSuccess?.();
      closeDialog();
    },
    onError: (e: unknown) => {
      toast.error(`Failed to update lead: ${e instanceof Error ? e.message : "Unknown error"}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.event_id) {
      toast.error("Email and Event are required");
      return;
    }
    if (mode === "add") {
      addLeadMutation.mutate();
    } else {
      updateLeadMutation.mutate();
    }
  };

  const isPending = addLeadMutation.isPending || updateLeadMutation.isPending;
  const isAddMode = mode === "add";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogTitleWithBug
          popupName={isAddMode ? "Add New Lead" : "Edit Lead"}
          popupContext="/events/leads"
          isOpen={isOpen}
        >
          {isAddMode ? "Add New Lead" : "Edit Lead"}
        </DialogTitleWithBug>
        <DialogDescription>
          {isAddMode
            ? "Manually add a lead to an event waiting list or pre-registration."
            : "Update lead information and status."}
        </DialogDescription>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="event">Event *</Label>
            <Select
              value={formData.event_id}
              onValueChange={(value) => updateFormData({ event_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an event" />
              </SelectTrigger>
              <SelectContent>
                {events.map((evt: { id: string; title: string }) => (
                  <SelectItem key={evt.id} value={evt.id}>
                    {evt.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => updateFormData({ email: e.target.value })}
              placeholder="lead@example.com"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => updateFormData({ name: e.target.value })}
              placeholder="John Doe"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => updateFormData({ phone: e.target.value })}
              placeholder="+1234567890"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => updateFormData({ type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_LEAD_TYPE_OPTIONS.map((opt) => (
                    <SelectItem 
                      key={opt.value} 
                      value={opt.value}
                      disabled={opt.value === 'engagement'}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => updateFormData({ status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UI_LEAD_STATUS_OPTIONS.map((opt) => (
                    <SelectItem 
                      key={opt.value} 
                      value={opt.value}
                      disabled={opt.disabled}
                    >
                      <span className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${opt.color}`} />
                        {opt.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => updateFormData({ notes: e.target.value })}
              placeholder="Additional notes about this lead..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="activity">Activity</Label>
            <Input
              id="activity"
              value={formData.activity}
              onChange={(e) => updateFormData({ activity: e.target.value })}
              placeholder="Activity identifier (optional)"
            />
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (isAddMode ? "Adding..." : "Saving...") : isAddMode ? "Add Lead" : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
